import "./TemplateCard.scss";

import { Button, Flex, Segment, Text } from "@fluentui/react-northstar";
import { TeamsQuestionnaireTemplateCategory } from "@teams/types";
import { QuestionnaireTemplateDTO } from "@teams/types/api";
import React, { useState } from "react";

import GeneralTemplateIcon from "../../../../_shared/assets/general-template-image.svg";
import HRTemplateIcon from "../../../../_shared/assets/hr-template-image.svg";
import ITTemplateIcon from "../../../../_shared/assets/it-template-image.svg";
import LeaderTemplateIcon from "../../../../_shared/assets/leader-template-image.svg";
import QuizIcon from "../../../../_shared/assets/pollytypes/quiz.svg";
import SurveyIcon from "../../../../_shared/assets/pollytypes/survey.svg";
import WFHTemplateIcon from "../../../../_shared/assets/wfh-template-image.svg";
import { useTheme } from "../../../../_shared/state/ThemeProvider";
import { TemplateDetailsDialog } from "./TemplateDetailsDialog";
import { TemplateManagementMenu } from "./TemplateManagementMenu";

export type TemplateCardProps = {
  template: QuestionnaireTemplateDTO;
  onSelect: (template: QuestionnaireTemplateDTO) => void;
  onDelete: (template: QuestionnaireTemplateDTO) => void;
  actionLabel: string;
};
export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onSelect,
  onDelete,
  actionLabel,
}) => {
  const { themeName } = useTheme();
  const [showDetailsDialog, setShowDetailsDialog] = useState<boolean>(false);

  return (
    <>
      <Segment color="brand" className="template-card">
        <div className="template-card__header">
          <Text
            className="template-card__title"
            content={template.templateName}
            weight="bold"
            size="large"
          />
          <Text truncated content={`💬${template.questions.length}`} />
        </div>

        <Flex className="template-card__categories">
          {template.categories?.map((category: TeamsQuestionnaireTemplateCategory) => (
            <TemplateCategoryComponent key={category} category={category} />
          ))}
        </Flex>

        <Flex
          className={`template-card__image-holder template-card__image-holder--theme-${themeName}`}
          vAlign="center"
          hAlign="center"
        >
          <TemplateIconComponent template={template} size="large" />
        </Flex>

        <Text
          className="template-card__description"
          as="div"
          content={template.templateDescription}
        />

        <Flex hAlign="end" className="template-card__management">
          <TemplateManagementMenu template={template} onDeleted={onDelete} />
        </Flex>

        <div className="template-card__details-footer">
          <Flex gap="gap.small">
            <Button
              data-tname="show-template-details"
              fluid
              size="medium"
              content="Details"
              onClick={() => setShowDetailsDialog(true)}
            />
            <Button
              data-tname="template-select"
              fluid
              size="medium"
              content={actionLabel}
              onClick={() => onSelect(template)}
              primary
            />
          </Flex>
        </div>
      </Segment>

      <TemplateDetailsDialog
        open={showDetailsDialog}
        template={template}
        onCancel={() => setShowDetailsDialog(false)}
        onConfirm={() => {
          onSelect(template);
          setShowDetailsDialog(false);
        }}
        actionLabel={actionLabel}
      />
    </>
  );
};

export type TemplateIconComponentProps = {
  template: QuestionnaireTemplateDTO;
  size: "small" | "large";
};
export const TemplateIconComponent: React.FC<TemplateIconComponentProps> = ({ template, size }) => {
  let length = size === "small" ? "40px" : "60px";

  let templateIcon;
  const category: TeamsQuestionnaireTemplateCategory | undefined = template.categories?.length
    ? template.categories[0]
    : undefined;
  switch (category) {
    case "General":
      templateIcon = GeneralTemplateIcon;
      break;
    case "HR":
      templateIcon = HRTemplateIcon;
      break;
    case "IT":
      templateIcon = ITTemplateIcon;
      break;
    case "Leader":
      templateIcon = LeaderTemplateIcon;
      break;
    case "WFH":
      templateIcon = WFHTemplateIcon;
      break;
    default:
      if (template.type === "quiz") {
        templateIcon = QuizIcon;
      } else {
        templateIcon = SurveyIcon;
      }
      length = "32px";
  }
  return <img src={templateIcon} alt={category || "Custom"} height={length} width={length} />;
};

export type TemplateCategoryComponentProps = {
  category: TeamsQuestionnaireTemplateCategory;
};
export const TemplateCategoryComponent: React.FC<TemplateCategoryComponentProps> = ({
  category,
}) => {
  return (
    <Text
      content={category}
      weight="bold"
      size="small"
      className={`template-card__category template-card__category-${category
        .toLowerCase()
        .replace(/\s/g, "-")}`}
    />
  );
};
